import { CREDIT_RATES, REWRITE_TIERS, countWords, type RewriteTier } from '@lyrmo/shared';
import { useState, type CSSProperties } from 'react';
import { ApiError } from '../../api/api-error';
import { Button } from '../../components/Button';
import { Icon, type IconName } from '../../components/Icon';
import { RollingNumber } from '../../components/RollingNumber';
import { Skeleton } from '../../components/Skeleton';
import type { CopyOutcome } from '../../hooks/useClipboard';
import { cx } from '../../lib/cx';
import { useI18n } from '../../i18n/i18n-context';
import { downloadText, textFileName } from '../../lib/download';
import { motionAllowed, withViewTransition } from '../../lib/motion';
import { useTrack } from '../../lib/tracking-context';
import { playTool } from './tool-motion';
import { DiffView } from './DiffView';
import { MarkdownText } from './MarkdownText';
import { ExplainView } from './ExplainView';
import { AltsView } from './AltsView';
import type { Suggest } from './useAlternatives';
import type { Explain } from './useExplanations';
import type { SpeechState } from './useSpeech';
import { VoiceWave } from './VoiceWave';

export type CardResult =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | {
      readonly status: 'ready';
      readonly text: string;
      readonly original: string;
      /** The version as the rewrite returned it, before other words were swapped in. */
      readonly returned: string;
    };

interface ResultCardProps {
  readonly tier: RewriteTier;
  readonly index: number;
  readonly result: CardResult;
  readonly copyOutcome: CopyOutcome | undefined;
  readonly onCopy: () => void;
  readonly onRetry: () => void;
  /** Makes the version the draft, to edit it or rewrite it again. */
  readonly onEdit: () => void;
  /** Whether it can be made the draft now; true unless said otherwise. */
  readonly editable?: boolean;
  /** In place of the last rewrite's card, there before it: it doesn't come in again. */
  readonly replacing?: boolean | undefined;
  /** Explains the version's changes; without it, the card has no Why view. */
  readonly onExplain?: Explain | undefined;
  /** Suggests other ways to say the version's changes; with onRevise, the card has Alts. */
  readonly onSuggest?: Suggest | undefined;
  /** Makes `text` the version, once other words are swapped in for its changes. */
  readonly onRevise?: ((text: string) => void) | undefined;
  readonly speech?: SpeechState | undefined;
  readonly onSpeak?: (() => void) | undefined;
  readonly onStopSpeech?: (() => void) | undefined;
  readonly onSaveText?: ((name: string, text: string) => void) | undefined;
  readonly onUse?: (() => void) | undefined;
  /** A visible host action label places the insert button below the version. */
  readonly useLabel?: string | undefined;
  readonly usable?: boolean | undefined;
  readonly retryable?: boolean | undefined;
}

type View = 'text' | 'diff' | 'why' | 'alts';

// Longer versions come in whole: so many words, one by one, would only keep the reader waiting.
const MAX_CASCADE_WORDS = 400;

// The accent bar strengthens with each tier, from a light touch to the optimal version.
const accentOpacity = (index: number) => 0.25 + (0.75 * index) / (REWRITE_TIERS.length - 1);

export function ResultCard({
  tier,
  index,
  result,
  copyOutcome,
  onCopy,
  onRetry,
  onEdit,
  editable = true,
  replacing = false,
  onExplain,
  onSuggest,
  onRevise,
  speech,
  onSpeak,
  onStopSpeech,
  onSaveText = downloadText,
  onUse,
  useLabel,
  usable = true,
  retryable = true,
}: ResultCardProps) {
  const { t, format, describeError } = useI18n();
  const [view, setView] = useState<View>('text');
  // The other words swapped in for the version's changes, by the change's index, in Alts.
  const [picks, setPicks] = useState<ReadonlyMap<number, string>>(() => new Map());
  const alts = onSuggest && onRevise && { suggest: onSuggest, revise: onRevise };
  const views: readonly View[] = [
    'text',
    'diff',
    ...(onExplain ? (['why'] as const) : []),
    ...(alts ? (['alts'] as const) : []),
  ];
  // The card grows or shrinks to the new view, and the versions below it move along smoothly.
  // A card made while its version was being written shows it arriving, word by word, the once.
  const [cascade, setCascade] = useState(result.status === 'loading');
  const track = useTrack();
  const showView = (next: View) => {
    if (next !== 'text' && next !== view) track({ event: 'result', view: next, version: tier });
    setCascade(false);
    withViewTransition('results', () => {
      setView(next);
    });
  };
  const { label, description } = t.tiers[tier];
  const number = String(index + 1);
  const titleId = `tier-${number}-title`;
  const bodyId = `tier-${number}-body`;
  const style = { '--tier-opacity': accentOpacity(index), '--tier-index': index } as CSSProperties;
  const speaking = speech?.status === 'loading' || speech?.status === 'playing';

  return (
    <article
      className={cx(
        'card card--tier result-card',
        result.status === 'error' && 'result-card--error',
        result.status === 'ready' && 'result-enter',
        replacing && 'is-replacing',
      )}
      style={style}
      aria-labelledby={titleId}
      aria-busy={result.status === 'loading'}
    >
      <header className="result-header">
        <div className="result-heading">
          <h3 className="result-title" id={titleId}>
            <span className="tier-number" aria-hidden="true">
              {number.padStart(2, '0')}
            </span>
            {label}
          </h3>
          <p className="result-description">{description}</p>
        </div>
        <div className="result-actions">
          {onSpeak && (
            <button
              type="button"
              className={cx(
                'button button--ghost icon-button speak-button',
                speaking && 'is-speaking',
              )}
              aria-label={speaking ? t.speech.stop(label) : t.speech.speak(label)}
              title={
                speaking
                  ? t.speech.stop(label)
                  : `${t.speech.speak(label)} · ${t.speech.aiVoice}${result.status === 'ready' ? ` · ${format.number(countWords(result.text) * CREDIT_RATES.speechPerWord)}${t.account.chipUnit(countWords(result.text) * CREDIT_RATES.speechPerWord)}` : ''}`
              }
              aria-pressed={speaking}
              disabled={result.status !== 'ready'}
              data-tool="speak"
              onClick={(event) => {
                playTool(event.currentTarget.parentElement, 'speak');
                (speaking ? onStopSpeech : onSpeak)?.();
              }}
            >
              {/* As on the draft: the voice, as a little spectrum; pointed at, Stop. */}
              {speaking ? (
                <>
                  <VoiceWave status={speech.status} />
                  <Icon name="stop" />
                </>
              ) : (
                <Icon name="speaker" />
              )}
            </button>
          )}
          <button
            type="button"
            className="button button--ghost icon-button"
            aria-label={t.card.edit(label)}
            title={t.card.editTitle(label)}
            disabled={result.status !== 'ready' || !editable}
            onClick={onEdit}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="button button--ghost icon-button"
            aria-label={t.card.download(label)}
            title={t.card.downloadTitle(label)}
            disabled={result.status !== 'ready'}
            onClick={() => {
              if (result.status === 'ready') onSaveText(textFileName(label), result.text);
            }}
          >
            <Icon name="download" />
          </button>
          <CopyButton
            label={label}
            outcome={copyOutcome}
            disabled={result.status !== 'ready'}
            onCopy={onCopy}
          />
          {onUse && !useLabel && (
            <button
              type="button"
              className="button button--ghost icon-button result-insert"
              aria-label={`Insert ${label}`}
              title={`Insert ${label}`}
              disabled={result.status !== 'ready' || !usable}
              onClick={onUse}
            >
              <Icon name="paste" />
            </button>
          )}
        </div>
      </header>

      <div
        key={`${result.status}:${view}`}
        className={cx(
          'result-body',
          result.status === 'ready' && view === 'diff' && 'is-diff',
          result.status === 'ready' && view === 'why' && 'is-explain',
          result.status === 'ready' && view === 'alts' && 'is-alts',
          result.status === 'ready' && (view === 'text' || view === 'diff') && 'rich-text',
        )}
        id={bodyId}
        dir="auto"
      >
        {result.status === 'loading' && <Skeleton />}
        {result.status === 'error' && (
          <>
            <p className="error-message">{t.card.failed}</p>
            <Button icon="retry" disabled={!retryable} onClick={onRetry}>
              {t.common.retry}
            </Button>
          </>
        )}
        {result.status === 'ready' &&
          (view === 'why' && onExplain ? (
            <ExplainView original={result.original} rewritten={result.text} explain={onExplain} />
          ) : view === 'alts' && alts ? (
            <AltsView
              original={result.original}
              returned={result.returned}
              picks={picks}
              suggest={alts.suggest}
              onPick={(next, text) => {
                setPicks(next);
                alts.revise(text);
              }}
            />
          ) : view === 'diff' ? (
            <DiffView original={result.original} rewritten={result.text} />
          ) : (
            // Formatted as the draft is, a version just written coming in word by word.
            <MarkdownText
              text={result.text}
              cascade={cascade && motionAllowed() && countWords(result.text) <= MAX_CASCADE_WORDS}
            />
          ))}
      </div>

      {/* What the voice is doing, unseen; a failure, in a note under the card's buttons. */}
      {speech && (
        <p className={speech.status === 'error' ? 'speech-note' : 'sr-only'} role="status">
          {speech.status === 'error'
            ? speech.error instanceof ApiError
              ? describeError(speech.error)
              : t.speech.failed
            : speech.status === 'loading'
              ? t.speech.loading
              : t.speech.aiVoice}
        </p>
      )}

      {result.status === 'ready' && (
        <footer className="result-footer">
          <div
            className="view-toggle"
            style={{ '--views': views.length, '--view': views.indexOf(view) } as CSSProperties}
            role="group"
            aria-label={t.card.views(label)}
          >
            {views.map((name) => (
              <Button
                key={name}
                aria-pressed={view === name}
                aria-controls={bodyId}
                onClick={() => {
                  showView(name);
                }}
              >
                {t.card[name]}
              </Button>
            ))}
          </div>
          <span className="result-meta">
            {view === 'text' ? (
              <RollingNumber value={t.counts.words(countWords(result.text))} />
            ) : view === 'diff' ? (
              t.card.compared
            ) : view === 'why' ? (
              t.card.whyEachChange
            ) : (
              t.card.otherWays
            )}
          </span>
          {onUse && useLabel && (
            <Button
              className="result-insert"
              icon="paste"
              aria-label={`Insert ${label}`}
              title={`Insert ${label}`}
              disabled={!usable}
              onClick={onUse}
            >
              {useLabel}
            </Button>
          )}
        </footer>
      )}
    </article>
  );
}

interface CopyButtonProps {
  readonly label: string;
  readonly outcome: CopyOutcome | undefined;
  readonly disabled: boolean;
  readonly onCopy: () => void;
}

// As on the draft, the icon alone says what happened: a check once copied, an alert if not.
const COPY_ICONS: Readonly<Record<CopyOutcome | 'idle', IconName>> = {
  copied: 'check',
  failed: 'alert',
  idle: 'copy',
};

function CopyButton({ label, outcome, disabled, onCopy }: CopyButtonProps) {
  const { t } = useI18n();
  const state = outcome ?? 'idle';
  const name = {
    copied: t.card.copied(label),
    failed: t.card.copyFailed(label),
    idle: t.card.copy(label),
  }[state];

  return (
    <button
      type="button"
      className={cx(
        'button button--ghost icon-button copy-button',
        outcome === 'copied' && 'is-copied',
        outcome === 'failed' && 'is-warning',
      )}
      aria-label={name}
      title={t.card.copy(label)}
      disabled={disabled}
      data-tool="copy"
      onClick={(event) => {
        playTool(event.currentTarget.parentElement, 'copy');
        onCopy();
      }}
    >
      <Icon name={COPY_ICONS[state]} />
    </button>
  );
}
